import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
  Watch,
  Listen,
  Fragment,
} from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { UiHubPages, UiNavigationBody, UiPage } from '../ui-cms-navigation/ui-cms-navigation';
import { UiHeaderServiceArea } from '../ui-cms-header-service-area/ui-cms-header-service-area';
import { DEFAULT_TRACKING_DELAY } from '../../../global/utility/tracking-helpers';

@Component({
  tag: 'ui-cms-offcanvas-navigation',
  styleUrl: 'ui-cms-offcanvas-navigation.scss',
  shadow: true,
})
export class UiCmsOffcanvasNavigation implements ComponentInterface {
  @Element() el: HTMLUiCmsOffcanvasNavigationElement;

  levelUpSelector!: HTMLButtonElement;

  mainNavigationList!: HTMLUListElement;

  openLevelSelector!: HTMLUListElement;

  rightOffCanvasMenu!: HTMLDivElement;

  navigationOC!: HTMLDivElement;

  currentLevel = 0;

  @State() searchActive: boolean;

  @Event() offcanvasNavReady: EventEmitter<HTMLUiCmsOffcanvasNavigationElement>;

  @Event() triggerExternalNavigation: EventEmitter<{ page: UiPage }>;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop() serviceArea: UiHeaderServiceArea = {};

  @Prop({ mutable: true }) body: UiNavigationBody;

  @Prop({ mutable: true }) i18n = { languageSwitchLabel: 'Sprachen' };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  // SET in ui-cms-header
  // allows default navigation on menu item click if TRUE
  // prevents default navigation on menu item click and emits triggerExternalNavigation event if FALSE
  @Prop({ attribute: 'navigateOnClick' }) navigateOnClick: boolean;

  @Event() offcanvasTrigger: EventEmitter<{ id: string }>;

  @Event() offcanvasInit: EventEmitter;

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
    this.offcanvasNavReady.emit(this.el);
  }

  componentDidLoad(): void {
    if (this.body?.site) {
      this.checkCurrentPath();
    }
    this.adjustOCHeight();
  }

  onSearchClear() {
    this.searchActive = false;
  }

  onSearchSuggest() {
    this.searchActive = true;
  }

  onLinkClick(e: any, page: UiPage) {
    if (!this.navigateOnClick) {
      e.preventDefault();
      e.stopPropagation();

      // emit event to handle navigation in external element
      this.triggerExternalNavigation.emit({ page });

      // call ui-cms-header method initOffcanvas
      // setTimeout ensures event fires after the ui-cms-navigation body has the correct active page
      setTimeout(() => this.offcanvasInit.emit(), 0);

      // fire event to close offcanvas panel from ui-offcanvas-provider
      this.offcanvasTrigger.emit({ id: 'offcanvas-navigation' });
    }

    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Off Canvas Navigation Link',
        eventType: 'Action',
      },
      category: {
        area: 'Navigation',
      },
      attributes: {
        elementLabel: page.title,
        elementType: 'Link',
        target: page.contentId,
      },
    };

    setTimeout(() => {
      window.ui?.services?.tracking?.track(trackingEvent);
    }, DEFAULT_TRACKING_DELAY);
  }

  // to open navigation on level where is current active page
  checkCurrentPath() {
    const { currentPage } = this;

    if (currentPage) {
      // all the <li> parents has to get this class
      currentPage.closest('li.has-children').classList.add('current');
      // getting data level  - needed for style
      const dataLevel = currentPage.getAttribute('data-level');
      // moving reference on new openLevelSelector
      this.openLevelSelector = currentPage.closest('ul') as HTMLUListElement;
      // adding classes and data needed for navigation to be visible
      this.openLevelSelector.classList.add('open-level');
      this.openLevelSelector.classList.add('show-level');
      this.openLevelSelector.closest('ul:not(.open-level)')?.classList.add('show-level');
      this.currentLevel = -100 * (parseInt(dataLevel, 10) - 1);
      this.mainNavigationList.style.marginLeft = `${this.currentLevel.toString()}%`;
      this.setLevelUpVisibility();
    }
  }

  get currentPage() {
    if (!this.mainNavigationList) return null;

    let level = 1;
    // only current active page has class '.current'
    const activeLevels = this.mainNavigationList?.querySelectorAll('li.current');
    let currentPage = activeLevels[0];
    activeLevels.forEach((element) => {
      if (parseInt(element.getAttribute('data-level'), 10) > level) {
        currentPage = element;
        level += 1;
      }
    });

    return currentPage;
  }

  openNewLevel(e, hasSubpages, page?: UiPage) {
    if (hasSubpages) {
      e.preventDefault();
      if (!this.openLevelSelector) {
        this.openLevelSelector = this.mainNavigationList;
      }
      this.openLevelSelector.classList.remove('open-level');
      this.openLevelSelector = e.currentTarget.nextElementSibling;
      this.openLevelSelector.classList.add('open-level');
      this.openLevelSelector.classList.add('show-level');
      this.currentLevel += -100;
      this.mainNavigationList.style.marginLeft = `${this.currentLevel.toString()}%`;
      this.setLevelUpVisibility();
      this.rightOffCanvasMenu.scrollTop = 0;
      this.adjustOCHeight();
    } else {
      this.onLinkClick(e, page);
    }
  }

  levelUp() {
    this.openLevelSelector.classList.remove('show-level', 'open-level');
    this.openLevelSelector = this.openLevelSelector.closest('.show-level') as HTMLUListElement;
    this.openLevelSelector.classList.add('open-level');
    this.currentLevel += +100;
    this.mainNavigationList.style.marginLeft = `${this.currentLevel.toString()}%`;
    this.setLevelUpVisibility();
    this.rightOffCanvasMenu.scrollTop = 0;
    this.adjustOCHeight();
  }

  setLevelUpVisibility() {
    this.levelUpSelector.style.visibility = this.currentLevel < 0 ? 'visible' : 'hidden';
  }

  adjustOCHeight() {
    if (!this.navigationOC) return;

    const el = this.navigationOC?.querySelector('.open-level.show-level') as HTMLUListElement;

    // eslint-disable-next-line max-len
    // adjust height for wrapping listcontainer (lists are positioned absolute and do not expand the wrapper)
    const listHeight = el ? el.offsetHeight + 100 : this.mainNavigationList.offsetHeight;

    this.navigationOC.style.height = `${listHeight}px`;
    this.navigationOC.setAttribute('data-height', listHeight.toString());
  }

  getFirstLevel(page: UiPage, dataLevel: number) {
    return (
      <li
        role="presentation"
        data-level={dataLevel}
        class={{
          'overview-link': true,
          'has-children': !!page.pages,
          active: page.active,
        }}
      >
        <a href={page.url} role="menuitem" class="menu-item" onClick={(e) => this.openNewLevel(e, page.pages, page)}>
          <strong>{page.title}</strong>
          <span class="menu-item-meta"></span>
        </a>
        {page.pages && (
          <ul class="menu-wrapper">
            <li class="overview-link">
              <a href={page.url} role="menuitem" class="menu-item" onClick={(e) => this.onLinkClick(e, page)}>
                <strong>{page.title}</strong>
                <span class="menu-item-meta"></span>
              </a>
            </li>
            {page.pages?.map((currentPage) => this.getSecondLevel(currentPage, dataLevel + 1))}
            {this.renderServiceArea()}
            {page.hubPages?.pages?.length && this.getHubNavigation(page.hubPages)}
          </ul>
        )}
      </li>
    );
  }

  getSecondLevel(page: UiPage, dataLevel: number) {
    return (
      <li
        class={{
          'has-children': !!page.pages,
          current: page.active,
        }}
        data-level={dataLevel}
      >
        <a role="menuitem" href={page.url} onClick={(e) => this.openNewLevel(e, page.pages, page)}>
          <strong>{page.title}</strong>
          <span class="menu-item-meta">{page.description}</span>
        </a>
        {page.pages && (
          <ul>
            <li class="overview-link">
              <a role="menuitem" href={page.url} class="menu-item" onClick={(e) => this.onLinkClick(e, page)}>
                <strong>{page.title}</strong>
                <span class="menu-item-meta">{page.description}</span>
              </a>
            </li>
            {page.pages?.map((currentPage) => this.getSecondLevel(currentPage, dataLevel + 1))}
            {this.serviceArea && this.renderServiceArea()}
          </ul>
        )}
      </li>
    );
  }

  getHubNavigation(hubPage: UiHubPages) {
    return (
      <li class="hub-navigation">
        <strong class="hub-navigation__headline">{hubPage.title}</strong>
        <ul>{hubPage.pages?.map((page) => this.getHubItem(page))}</ul>
      </li>
    );
  }

  getHubItem(page: UiPage) {
    return (
      <li>
        <article class="hub-link-wrapper">
          <a href={page.url} target="_blank" rel="noopener noreferrer" onClick={(e) => this.onLinkClick(e, page)}>
            <h2>{page.title}</h2>
            <p>{page.description}</p>
          </a>
        </article>
      </li>
    );
  }

  renderLink(link) {
    return (
      <li class="service-nav" role="presentation" key={link.title}>
        <a href={link.src} target={link.target}>
          <ui-icon icon={link.iconOption !== 'none' ? link.icon : null} type="system"></ui-icon>
          {link.title}
        </a>
      </li>
    );
  }

  renderDropdown(dropdownItem) {
    return (
      <li role="presentation" class="has-flyout-layer has-children service-nav" key={dropdownItem.title}>
        <a class="flyout-layer-trigger" onClick={(e) => this.openNewLevel(e, true)}>
          {dropdownItem.icon && dropdownItem.iconOption === 'icon' ? (
            <ui-icon icon={dropdownItem.icon} type="system"></ui-icon>
          ) : null}
          {dropdownItem.title}
        </a>
        <ul class="menu-wrapper">
          <li class="overview-link not-linked" key={dropdownItem.title}>
            <span class="topic"> {dropdownItem.title} </span>
          </li>
          {dropdownItem.dropdownLinks?.map((link) => this.renderLink(link))}
        </ul>
      </li>
    );
  }

  renderLanguageSwitch() {
    const { languageLinks, languageSwitchIcon } = this.serviceArea;

    if (!languageLinks || languageLinks.length === 0) {
      return null;
    }
    if (languageLinks.length === 1) {
      const link = languageLinks[0];
      return this.renderLink({ ...link, iconOption: 'icon', icon: languageSwitchIcon || 'globe' });
    }
    if (languageLinks.length > 1) {
      return this.renderDropdown({
        title: this.i18n.languageSwitchLabel,
        icon: languageSwitchIcon || 'globe',
        dropdownLinks: languageLinks,
        iconOption: 'icon',
        trackingReferenceID: null,
        titleDisplayPosition: 'title-next-to-icon',
      });
    }
    return null;
  }

  renderServiceArea() {
    const { singleLinks, dropdowns, languageLinks } = this.serviceArea;

    return [
      singleLinks && singleLinks?.map((link) => this.renderLink(link)),
      dropdowns && dropdowns?.map((dropdownItem) => this.renderDropdown(dropdownItem)),
      languageLinks && this.renderLanguageSwitch(),
    ];
  }

  checkPositionAndHeight() {
    this.checkCurrentPath();
    this.adjustOCHeight();
  }

  onSearch(): void {
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventType: 'Action',
        eventName: 'Search Submit',
      },
      category: {
        area: 'Header',
      },
      attributes: {
        elementLabel: 'Off Canvas Navigation Search Field',
        elementType: 'Button',
        target: `${this.body.site.title}.Suchen.Suchergebnisseite`,
      },
    };

    if (trackingEvent) {
      window.ui?.services?.tracking?.track(trackingEvent);
    }
  }

  @Listen('click', { target: 'window' })
  closeSearchOnClickOutside(ev) {
    if (!this.searchActive) {
      return;
    }
    if (ev.path.some((el) => el === this.el) && ev.path.some((el) => el.className === 'search')) {
      return;
    }

    this.searchActive = false;
  }

  render() {
    const site = this.body?.site;
    return (
      <div
        key={2}
        class="offcanvas-navigation main"
        ref={(el) => {
          this.rightOffCanvasMenu = el as HTMLDivElement;
        }}
      >
        <div class="inner-offcanvas">
          <div class="off-canvas-functions clearfix">
            <button
              class="level-up"
              aria-label="zurück"
              onClick={this.levelUp.bind(this)}
              ref={(el) => {
                this.levelUpSelector = el;
              }}
            >
              <ui-icon icon="arrow-left" type="system"></ui-icon>
            </button>
            <ui-offcanvas-trigger offcanvas-id="offcanvas-navigation">
              <button class="close-offcanvas" aria-label="schließen">
                <ui-icon icon="close" type="system"></ui-icon>
              </button>
            </ui-offcanvas-trigger>
          </div>
          {this.serviceArea.search && (
            <div class="search">
              <ui-cms-search-autosuggest
                onClear={this.onSearchClear.bind(this)}
                onSuggest={this.onSearchSuggest.bind(this)}
                searchUrl={this.serviceArea.search.targetPageUrl}
                suggestionUrl={this.serviceArea.search.suggestionUrl}
                onSearch={this.onSearch.bind(this)}
              ></ui-cms-search-autosuggest>
            </div>
          )}

          <div
            class={{
              'off-canvas-nav-wrapper': true,
              navigation: true,
              hidden: this.searchActive,
            }}
          >
            <nav
              role="navigation"
              class="main"
              ref={(el) => {
                this.navigationOC = el as HTMLDivElement;
              }}
            >
              <ul
                role="menubar"
                data-height="false"
                class="show-level"
                ref={(el) => {
                  this.mainNavigationList = el as HTMLUListElement;
                }}
              >
                {site && (
                  <Fragment>
                    <li>
                      <a href={site.url} onClick={(e) => this.onLinkClick(e, site)}>
                        <strong>{site.title}</strong>
                      </a>
                    </li>
                    {site.pages?.map((singlePage) => this.getFirstLevel(singlePage, 1))}
                  </Fragment>
                )}
                {this.serviceArea && this.renderServiceArea()}
                {this.checkPositionAndHeight()}
              </ul>
            </nav>
          </div>
        </div>
      </div>
    );
  }
}
